// eslint-disable-next-line jira/restricted/react
import React, { PureComponent, type ComponentType, type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Lozenge from '@atlaskit/lozenge';
// eslint-disable-next-line import/order
import { Flex, xcss } from '@atlaskit/primitives';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import BluePrintComponent from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/add-on-v1';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import {
	fireUIAnalytics,
	ContextualAnalyticsData,
	AnalyticsEventToProps,
} from '@atlassian/jira-product-analytics-bridge';
import { Link } from '@atlassian/react-resource-router';
import messages from './messages';
import {
	Container,
	DetailsContainer,
	Description,
	NameContainer,
	DisabledLozengeContainer,
	DetailsLinkContainer,
} from './styled';

export type Props = {
	name: string;
	description: string;
	disabled: boolean;
	url: string;
	ruleSetId: string;
	validationErrorMessages: string[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ActionsMenuComponent: ComponentType<any>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	DeleteDialogComponent: ComponentType<any>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ValidationErrorComponent: ComponentType<any>;
	onOpenDelete: () => void;
	onDuplicate: () => void;
	onViewLog: () => void;
	intl: IntlShape;
};

const LinkWithAnalytics = AnalyticsEventToProps('link', {
	onClick: 'clicked',
})(Link);

// eslint-disable-next-line jira/react/no-class-components
class RuleListItem extends PureComponent<Props> {
	static defaultProps = {
		disabled: false,
	};

	onEditRule = (_: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'ruleLink');
	};

	render() {
		const {
			name,
			description,
			url,
			ruleSetId,
			disabled,
			onOpenDelete,
			onDuplicate,
			onViewLog,
			validationErrorMessages,
			intl: { formatMessage },
			ActionsMenuComponent,
			DeleteDialogComponent,
			ValidationErrorComponent,
		} = this.props;
		return (
			<ContextualAnalyticsData attributes={{ automationRuleId: ruleSetId }}>
				<Container data-test-id="servicedesk-automation-summary.layout.rule-list.rule">
					<Flex justifyContent="center" direction="column" xcss={boxContainercss}>
						<BluePrintComponent aria-hidden alt="" />
					</Flex>
					<DetailsContainer>
						<NameContainer>
							<DetailsLinkContainer>
								<LinkWithAnalytics onClick={this.onEditRule} to={url}>
									{name}
								</LinkWithAnalytics>
							</DetailsLinkContainer>
							{disabled && (
								<DisabledLozengeContainer>
									<Lozenge> {formatMessage(messages.disabled)} </Lozenge>
								</DisabledLozengeContainer>
							)}
						</NameContainer>
						<Description>{description}</Description>
					</DetailsContainer>
					{validationErrorMessages ? (
						<ValidationErrorComponent errorMessages={validationErrorMessages} />
					) : null}
					<ActionsMenuComponent
						ruleSetId={ruleSetId}
						onDelete={onOpenDelete}
						onDuplicate={onDuplicate}
						onViewLog={onViewLog}
					/>
					<DeleteDialogComponent ruleSetId={ruleSetId} />
				</Container>
			</ContextualAnalyticsData>
		);
	}
}

export default injectIntl(RuleListItem);

const boxContainercss = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 8}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 8}px`,
	margin: 'auto',
});
